import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box } from '@mui/material';
import WaveCanvas from './WaveCanvas';

interface WaveformProps {
  barWidth: number;
  color?: string;
  duration: number;
  gradientColors?: (number | string)[][];
  height?: number;
  onClick?: (seconds: number) => void;
  peaks: number[];
  pixelRatio?: number;
  pos: number;
  progressColor?: string;
  progressGradientColors?: (number | string)[][];
  transitionDuration?: number;
}

const defaultPixelRatio =
  typeof window !== 'undefined' ? window.devicePixelRatio : 1;

const Waveform: React.FC<WaveformProps> = ({
  barWidth,
  color,
  duration,
  gradientColors,
  height = 30,
  onClick = () => {},
  peaks,
  pixelRatio = defaultPixelRatio,
  pos,
  progressColor = '#555555',
  progressGradientColors,
  transitionDuration = 200,
}) => {
  const [width, setWidth] = useState(0);
  const [waveWidth, setWaveWidth] = useState(0);
  const [waveHeight, setWaveHeight] = useState(height * pixelRatio);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const resize = useCallback(() => {
    if (wrapperRef.current) {
      const containerWidth = wrapperRef.current.clientWidth;
      setWidth(containerWidth);
      setWaveWidth(containerWidth * pixelRatio);
      setWaveHeight(height * pixelRatio);
    }
  }, [height, pixelRatio]);

  useEffect(() => {
    resize();
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
    };
  }, [resize]);

  useEffect(() => {
    setWaveHeight(height * pixelRatio);
  }, [height, pixelRatio]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (wrapperRef.current) {
      const percentageOffsetX = e.nativeEvent.offsetX / width;
      onClick(Math.round(percentageOffsetX * duration));
    }
  };

  const progressWidth = useCallback(() => {
    const percentageComplete = pos / duration;
    return width * percentageComplete;
  }, [pos, duration, width]);

  const progressWaveStyle = useCallback(
    () =>
      ({
        position: 'absolute',
        zIndex: 2,
        left: 0,
        top: 0,
        bottom: 0,
        overflow: 'hidden',
        height: `${height}px`,
        width: `${progressWidth()}px`,
        display: 'block',
        transition: `width ${transitionDuration}ms ease-in-out`,
        boxSizing: 'border-box',
      }) as React.CSSProperties,
    [height, progressWidth, transitionDuration]
  );

  return (
    <Box
      ref={wrapperRef}
      onClick={handleClick}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        cursor: 'pointer',
      }}
    >
      <WaveCanvas
        color={color}
        barWidth={barWidth}
        gradientColors={gradientColors}
        peaks={peaks}
        width={waveWidth}
        height={waveHeight}
      />
      <Box style={progressWaveStyle()}>
        <WaveCanvas
          barWidth={barWidth}
          color={progressColor}
          gradientColors={progressGradientColors}
          peaks={peaks}
          width={waveWidth}
          height={waveHeight}
        />
      </Box>
    </Box>
  );
};

export default Waveform;
